import * as _ from 'lodash-es';
import React from 'react';
import { useApolloClient } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import { UserType } from '../graphql/generated/graphql';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import useCurrentPostTopicIds from '../post/useCurrentPostTopicIds';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { miscToasts } from '../toasts/toasts';
import NewTopicModal from '../topic/modal/NewTopicModal';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useAllTopics, { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import addTestId from '../utils/addTestId';
import useSlug from '../utils/router/useSlug';
import SidebarExternalPostsSection from './SidebarExternalPostsSection';
import SidebarItems, { POST_FRAGMENT } from './SidebarItems';
import SidebarRow from './SidebarRow';
import SidebarSection from './SidebarSection';
import SidebarSectionIcon from './SidebarSectionIcon';
import SidebarSectionOverflowMenuItems from './SidebarSectionOverflowMenuItems';
import * as styles from './SidebarSections.module.scss';
import {
  PremadeSections,
  getSidebarSectionNameWithFallback,
  useSidebarSectionsContext,
  useUpdateSidebarSectionIds,
} from './SidebarSectionsProvider';
import isNotDetached from './isNotDetached';
import useCanManageSidebarSections from './useCanManageSidebarSections';

const CreateTopicButton = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;
  const dispatch = useAppDispatch();

  if (isGuest) return null;

  return (
    <div
      className={styles.addWrapper}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(UIActions.openModal(NewTopicModal));
      }}
    >
      <Tooltip content="Create a new topic">
        <div {...addTestId('addTopicButton')} className={styles.add}>
          <SVGIcon size={9}>{addIcon}</SVGIcon>
        </div>
      </Tooltip>
    </div>
  );
};

interface SidebarAllTopicsProps {
  allTopics: AllTopicsTreeListTopic[];
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  onMoveSectionUp?: () => void;
  onMoveSectionDown?: () => void;
}

const SidebarAllTopics = ({
  allTopics,
  activeTopics,
  expandedTopics,
  onMoveSectionUp,
  onMoveSectionDown,
}: SidebarAllTopicsProps) => {
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  return (
    <SidebarSection
      id="allTopics"
      title="All Topics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.ALL_TOPICS }} />}
      menuItems={
        isMobile ? undefined : (
          <SidebarSectionOverflowMenuItems
            selectedSectionId={PremadeSections.ALL_TOPICS}
            onMoveSectionUp={onMoveSectionUp}
            onMoveSectionDown={onMoveSectionDown}
          />
        )
      }
      hasChildren={allTopics.length > 0}
      customMenu={<CreateTopicButton />}
    >
      <SidebarItems
        topics={allTopics}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        sectionId={PremadeSections.ALL_TOPICS}
      />
    </SidebarSection>
  );
};

interface SidebarMyTopicsProps {
  title: string;
  myTopics: AllTopicsTreeListTopic[];
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  onMoveSectionUp?: () => void;
  onMoveSectionDown?: () => void;
}

const SidebarMyTopics = ({
  title,
  myTopics,
  activeTopics,
  expandedTopics,
  onMoveSectionUp,
  onMoveSectionDown,
}: SidebarMyTopicsProps) => {
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  return (
    <SidebarSection
      id="myTopics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.MY_TOPICS }} />}
      title={title}
      menuItems={
        isMobile ? undefined : (
          <SidebarSectionOverflowMenuItems
            selectedSectionId={PremadeSections.MY_TOPICS}
            onMoveSectionUp={onMoveSectionUp}
            onMoveSectionDown={onMoveSectionDown}
          />
        )
      }
      customMenu={<CreateTopicButton />}
      hasChildren={myTopics.length > 0}
    >
      <SidebarItems
        topics={myTopics}
        orderIds={myTopics.map((topic) => topic.id)}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        sectionId={PremadeSections.MY_TOPICS}
      />
    </SidebarSection>
  );
};

type TopicStates = {
  active?: Record<string, boolean>;
  expanded?: Record<string, boolean>;
};

const getTopicStates = (
  activeId: string,
  topics: AllTopicsTreeListTopic[],
): TopicStates | null =>
  topics.reduce((result: TopicStates | null, { id, childTopics }) => {
    if (result) return result;

    if (activeId === id) {
      return { active: { [id]: true } };
    }

    const childResult = getTopicStates(activeId, childTopics);

    if (childResult) {
      return {
        active: childResult.active,
        expanded: {
          [id]: true,
          ...childResult.expanded,
        },
      };
    }

    return null;
  }, null);

const getAllTopicStates = (
  ids: string[],
  topics: AllTopicsTreeListTopic[],
): Required<TopicStates> => {
  const states = ids.map((id) => getTopicStates(id, topics));
  return _.merge({ expanded: {}, active: {} }, ...states);
};

const SidebarSections = () => {
  const [topicId] = useSlug('topicSlug');
  const { sidebarSections, myTopics, allTopics } = useSidebarSectionsContext();
  const postTopicIds = useCurrentPostTopicIds();

  const treeTopics = useAllTopics();

  const activeTopicIds = topicId ? [topicId] : postTopicIds;
  const { expanded, active: activeTopics } = getAllTopicStates(
    activeTopicIds,
    treeTopics,
  );

  const dispatch = useAppDispatch();
  const allTopicsMap = useAllTopicMap();
  const allSlimPostsMap = useAllSlimPostMap();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [updateSidebarSectionIds] = useUpdateSidebarSectionIds();

  async function moveSection(
    section: (typeof sidebarSections)[number],
    direction: 'down' | 'up',
  ) {
    try {
      const index = sidebarSections.findIndex((item) => item.id === section.id);
      const newIndex = index + (direction === 'up' ? -1 : 1);
      if (index < 0 || newIndex < 0 || newIndex >= sidebarSections.length) {
        return;
      }

      const sectionIds = sidebarSections
        .toSpliced(index, 1)
        .toSpliced(newIndex, 0, section)
        .map((item) => item.id);
      await updateSidebarSectionIds({ variables: { sectionIds } });
    } catch (error) {
      dispatch(UIActions.pushToast(miscToasts.genericError));
    }
  }

  const getMoveSectionHandlers = (
    section: (typeof sidebarSections)[number],
  ) => ({
    onMoveSectionUp:
      sidebarSections.at(0)?.id === section.id
        ? undefined
        : () => moveSection(section, 'up'),
    onMoveSectionDown:
      sidebarSections.at(-1)?.id === section.id
        ? undefined
        : () => moveSection(section, 'down'),
  });

  const client = useApolloClient();
  const canManageSidebarSections = useCanManageSidebarSections();

  return (
    <>
      {canManageSidebarSections && sidebarSections.length === 0 && (
        <SidebarRow
          level={0}
          icon={editIcon}
          onClick={() =>
            dispatch(UIActions.setSelectedSectionId(PremadeSections.MY_TOPICS))
          }
        >
          Manage sections
        </SidebarRow>
      )}
      {sidebarSections.map((section) => {
        if (section.id === PremadeSections.ALL_TOPICS) {
          return (
            <SidebarAllTopics
              key={PremadeSections.ALL_TOPICS}
              allTopics={allTopics}
              expandedTopics={expanded}
              activeTopics={activeTopics}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        if (section.id === PremadeSections.MY_TOPICS) {
          return (
            <SidebarMyTopics
              key={PremadeSections.MY_TOPICS}
              title={section.name}
              myTopics={myTopics}
              expandedTopics={expanded}
              activeTopics={activeTopics}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        if (section.id === PremadeSections.INTEGRATIONS) {
          return (
            <SidebarExternalPostsSection
              key={PremadeSections.INTEGRATIONS}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        const posts = _.compact(
          section.items
            .filter((item) => item.__typename === 'Post')
            .map((item) => {
              const cachedPost =
                client.readFragment({
                  fragment: POST_FRAGMENT,
                  id: client.cache.identify({
                    __typename: 'Post',
                    id: item.id,
                  }),
                  fragmentName: 'SidebarItems_post',
                }) ?? allSlimPostsMap[item.id];

              if (!cachedPost) return null;
              return {
                ...cachedPost,
                title: cachedPost.title || 'Untitled',
              };
            }),
        );

        const topics = _.compact(
          section.items.map((item) =>
            item.__typename === 'Topic' ? allTopicsMap[item.id] : null,
          ),
        ).filter(isNotDetached);

        return (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={getSidebarSectionNameWithFallback(section.name)}
            collapsedByDefault
            icon={<SidebarSectionIcon section={section} />}
            menuItems={
              !isMobile && (
                <SidebarSectionOverflowMenuItems
                  selectedSectionId={section.id}
                  {...getMoveSectionHandlers(section)}
                />
              )
            }
            hasChildren={section.items.length > 0}
          >
            <SidebarItems
              activeTopics={activeTopics}
              expandedTopics={expanded}
              posts={posts}
              orderIds={section.items.map((item) => item.id)}
              topics={topics}
              sectionId={section.id}
            />
          </SidebarSection>
        );
      })}
    </>
  );
};

export default SidebarSections;
